import {
  applyTawkPosition,
  isTawkMobileViewport,
  TAWK_READY_EVENT,
} from './tawkPosition';

const TAWK_POLL_INTERVAL_MS = 100;
const TAWK_MAX_POLL_ATTEMPTS = 120;

function isTawkSupportRoute(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname.includes('/home') ||
    pathname.includes('/inplay')
  );
}

function shouldShowTawkOnRoute(pathname: string): boolean {
  return isTawkMobileViewport() ? isTawkSupportRoute(pathname) : true;
}

/** Sync Tawk bubble visibility with the current route (used by AppLayout). */
export function applyTawkVisibility(pathname: string): boolean {
  applyTawkPosition();

  if (!window.isTawkEnabled) return false;

  const shouldShow = shouldShowTawkOnRoute(pathname);
  window.__tawkShouldShow = shouldShow;
  return window.setTawkWidgetVisible?.(shouldShow) ?? false;
}

export function watchTawkVisibility(pathname: string): () => void {
  let positionObserver: MutationObserver | null = null;
  let pollId: number | undefined;

  const apply = () => applyTawkVisibility(pathname);

  const onTawkReady = () => apply();
  const startPositionObserver = () => {
    positionObserver?.disconnect();
    positionObserver = new MutationObserver(applyTawkPosition);
    const target =
      document.getElementById('tawk-bubble-container')?.parentElement ??
      document.body;
    positionObserver.observe(target, { childList: true, subtree: true });
  };

  const cleanup = () => {
    window.removeEventListener(TAWK_READY_EVENT, onTawkReady);
    window.removeEventListener(TAWK_READY_EVENT, startPositionObserver);
    positionObserver?.disconnect();
    if (pollId !== undefined) window.clearInterval(pollId);
  };

  window.addEventListener(TAWK_READY_EVENT, onTawkReady);

  if (window.isTawkEnabled) {
    startPositionObserver();
  } else {
    window.addEventListener(TAWK_READY_EVENT, startPositionObserver, {
      once: true,
    });
  }

  if (!apply()) {
    let attempts = 0;
    pollId = window.setInterval(() => {
      attempts += 1;
      if (apply() || attempts >= TAWK_MAX_POLL_ATTEMPTS) {
        window.clearInterval(pollId!);
      }
    }, TAWK_POLL_INTERVAL_MS);
  }

  return cleanup;
}

/** React hook helper — true only after Tawk script has loaded successfully. */
export function subscribeTawkReady(onReady: () => void): () => void {
  if (window.isTawkEnabled) {
    onReady();
    return () => undefined;
  }

  const handler = () => onReady();
  window.addEventListener(TAWK_READY_EVENT, handler);
  return () => window.removeEventListener(TAWK_READY_EVENT, handler);
}
